import * as React from 'react'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import Alert from '@mui/joy/Alert'
import { CircularProgress } from '@mui/joy'
import { documentProcessingTestApi, PipelineJobRow, SelectedDocument } from '../api'

const truncate = (text: string | null | undefined, maxLength = 22): string => (!text ? '' : text.length <= maxLength ? text : `${text.slice(0, maxLength)}…`)

interface Props {
    onSelect: (doc: SelectedDocument) => void
}

/**
 * Tab 1 — documents already run through THIS pipeline (Postgres document_jobs).
 * Self-growing: starts empty, grows every time this page (or production)
 * processes a document. Reuses the existing triage board's list endpoint —
 * no new backend code for this source.
 */
export default function PipelineJobsPicker({ onSelect }: Props) {
    const [projectName, setProjectName] = React.useState('')
    const [rows, setRows] = React.useState<PipelineJobRow[]>([])
    const [loading, setLoading] = React.useState(false)
    const [error, setError] = React.useState<string | null>(null)

    const load = React.useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const data = await documentProcessingTestApi.listPipelineJobs({ projectName: projectName.trim() || undefined })
            setRows(data)
        } catch {
            setError('Failed to load recent pipeline jobs')
        } finally {
            setLoading(false)
        }
    }, [projectName])

    React.useEffect(() => {
        load().catch(() => undefined)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                <FormControl size="sm" sx={{ flex: 1 }}>
                    <FormLabel>Filter by Project Name</FormLabel>
                    <Input size="sm" value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder="optional" />
                </FormControl>
                <Button size="sm" variant="outlined" onClick={() => load()} disabled={loading}>
                    {loading ? <CircularProgress size="sm" /> : 'Refresh'}
                </Button>
            </Box>

            {error && (
                <Alert color="danger" variant="soft">
                    {error}
                </Alert>
            )}

            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto', maxHeight: 320 }}>
                <Table size="sm" stickyHeader hoverRow>
                    <thead>
                        <tr>
                            <th style={{ width: 140 }}>Project</th>
                            <th style={{ width: 160 }}>Filename</th>
                            <th style={{ width: 90 }}>Doc Type</th>
                            <th style={{ width: 90 }}>Status</th>
                            <th style={{ width: 150 }}>Enqueued</th>
                            <th style={{ width: 80 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.id}>
                                <td>
                                    <Typography level="body-xs">{truncate(row.project_name)}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{truncate(row.filename) || '—'}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.doc_type_hint || '—'}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{row.status}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{new Date(row.enqueued_at).toLocaleString()}</Typography>
                                </td>
                                <td>
                                    <Button
                                        size="sm"
                                        variant="plain"
                                        onClick={() =>
                                            onSelect({
                                                contentHash: row.content_hash,
                                                blobUri: row.blob_uri,
                                                filename: row.filename ?? undefined,
                                                projectName: row.project_name,
                                            })
                                        }
                                    >
                                        Use
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        {!loading && rows.length === 0 && (
                            <tr>
                                <td colSpan={6} style={{ textAlign: 'center', padding: '24px' }}>
                                    <Typography level="body-sm" color="neutral">
                                        No documents yet — try the Ratecon Corpus tab or upload a new document.
                                    </Typography>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Sheet>
        </Box>
    )
}
